import Loading from "@/components/Loading/Loading"
import { usePublicCertificate } from "@/queries/queries"
import { useTranslation } from "react-i18next"
import {
  FaDownload,
  FaShareAlt,
  FaLink,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
} from "react-icons/fa"
import { useParams } from "react-router-dom"
import html2canvas from "html2canvas"
import { Dropdown } from "react-bootstrap"
import { toast } from "react-toastify"

export default function CertificateShare() {
  const { uuid } = useParams<{ uuid: string }>()
  const certificateQuery = usePublicCertificate(uuid || "")
  const { t } = useTranslation()

  const handleDownload = async () => {
    const element = document.getElementById("certificate-card")
    if (!element) return

    const canvas = await html2canvas(element, {
      useCORS: true,
      scale: 2,
      backgroundColor: null,
    })

    const image = canvas.toDataURL("image/png")
    const link = document.createElement("a")
    link.download = `certificate-${certificateQuery.data?.user.name}.png`
    link.href = image
    link.click()
  }

  const handleShare = (
    platform: "facebook" | "twitter" | "linkedin" | "copy"
  ) => {
    const url = window.location.href
    const message = t(
      "certificates.shareText",
      "I've earned a certificate in {{course}}!",
      { course: certificateQuery.data?.course.title }
    )

    if (platform === "copy") {
      navigator.clipboard.writeText(`${message} ${url}`)
      toast.success(
        t("certificates.copied", "Link and message copied to clipboard!")
      )
      return
    }

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }

    window.open(shareUrls[platform], "_blank", "width=600,height=400")
  }

  const sharePlatforms = [
    {
      key: "copy" as const,
      icon: <FaLink className="text-secondary" />,
      label: t("buttons.copyLink"),
    },
    {
      key: "facebook" as const,
      icon: <FaFacebookF className="text-primary" />,
      label: t("buttons.facebook"),
    },
    {
      key: "twitter" as const,
      icon: <FaTwitter className="text-info" />,
      label: t("buttons.twitter"),
    },
    {
      key: "linkedin" as const,
      icon: <FaLinkedinIn style={{ color: "#0077b5" }} />,
      label: t("buttons.linkedin"),
    },
  ]

  if (certificateQuery.isLoading) return <Loading />
  if (!certificateQuery.data)
    return (
      <div className="text-center p-5">
        {t("certificates.notFound", "Certificate not found")}
      </div>
    )

  const certificate = certificateQuery.data

  return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4 bg-light">
      <div
        id="certificate-card"
        className="position-relative overflow-hidden shadow-lg p-3 text-center d-flex flex-column align-items-center bg-white"
        style={{
          width: "100%",
          maxWidth: "800px",
          borderRadius: "15px",
        }}
      >
        <img
          src={certificate.image_url}
          alt="Certificate"
          className="img-fluid rounded shadow-sm mb-4"
          crossOrigin="anonymous"
        />

        <div className="w-100 text-start px-3 mb-4">
          <h2 className="fw-bold">{certificate.user.name}</h2>
          <p className="text-muted mb-0">{certificate.course.title}</p>
          <p className="text-muted small">{certificate.round.title}</p>
        </div>
      </div>

      <div className="mt-4 d-flex gap-3">
        <button
          onClick={handleDownload}
          className="btn btn-outline-primary btn-lg d-flex align-items-center gap-2"
          style={{ borderRadius: "10px" }}
        >
          <FaDownload /> {t("buttons.download", "Download as Image")}
        </button>

        <Dropdown align="end">
          <Dropdown.Toggle
            variant="primary"
            size="lg"
            className="rounded-pill d-flex align-items-center gap-2 border-0 shadow-sm px-4"
            style={{ borderRadius: "10px" }}
          >
            <FaShareAlt />
            <span>{t("buttons.share")}</span>
          </Dropdown.Toggle>

          <Dropdown.Menu className="border-0 shadow-lg rounded-3">
            {sharePlatforms.map(({ key, icon, label }) => (
              <Dropdown.Item
                key={key}
                onClick={() => handleShare(key)}
                className="d-flex align-items-center gap-2 py-2"
              >
                {icon} {label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="mt-4 text-muted small">
        {t("certificates.verifiedBy", "Verified by EraaSoft")}
      </div>
    </div>
  )
}
